import { useState, useEffect, useCallback } from 'react';
import { useParams, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS } from '../../styles/tokens';
import type { ColorTheme } from '../../styles/tokens';
import { fetchClients, fetchRoleUserCount } from '../../api/keycloak-admin';
import type { KeycloakClient } from '../../api/keycloak-admin';
import type { AppDetailContext } from './app-detail-context';
import { INSTANCE_ENABLED_APP_IDS } from '../../constants/apps';

/** Sub-tabs for the app detail view */
function getSubTabs(clientId: string): Array<{ label: string; path: string }> {
  const tabs = [
    { label: 'Overview', path: '' },
    { label: 'Access', path: 'access' },
  ];

  if (INSTANCE_ENABLED_APP_IDS.has(clientId)) {
    tabs.push({ label: 'Instances', path: 'instances' });
  }

  tabs.push({ label: 'Settings', path: 'settings' });
  return tabs;
}

/** App detail page with sub-tab navigation */
export default function AppDetail(): React.JSX.Element {
  const { appId } = useParams<{ appId: string }>();
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const navigate = useNavigate();

  const [client, setClient] = useState<KeycloakClient | null>(null);
  const [userCount, setUserCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const clients = await fetchClients(token);
      const found = clients.find((c) => c.id === appId);
      if (!found) {
        navigate('/apps', { replace: true });
        return;
      }
      setClient(found);
      const count = await fetchRoleUserCount(token, `app:${found.clientId}`);
      setUserCount(count);
    } catch {
      navigate('/apps', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [appId, token, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading || !client) {
    return <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>Loading...</p>;
  }

  const subTabs = getSubTabs(client.clientId);
  const context: AppDetailContext = { client, userCount, reload: loadData };

  return (
    <div>
      <Breadcrumb appName={client.name || client.clientId} colors={colors} />
      <SubTabNav tabs={subTabs} isDark={isDark} />
      <div style={{ marginTop: '20px' }}>
        <Outlet context={context} />
      </div>
    </div>
  );
}

/** Breadcrumb: Admin > Apps > App Name */
function Breadcrumb({ appName, colors }: { appName: string; colors: ColorTheme }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
      <NavLink to="/apps" style={{
        fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary,
        textDecoration: 'none',
      }}>
        Apps
      </NavLink>
      <span style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary }}>/</span>
      <span style={{ fontFamily: FONTS.heading, fontSize: '12px', color: colors.text, letterSpacing: '0.05em' }}>
        {appName}
      </span>
    </div>
  );
}

/** Sub-tab navigation within app detail */
function SubTabNav({ tabs, isDark }: {
  tabs: Array<{ label: string; path: string }>; isDark: boolean;
}): React.JSX.Element {
  return (
    <nav className="vx-tab-scroller" style={{
      display: 'flex', gap: '4px',
      borderBottom: `1px solid ${isDark ? COLORS.dark.border : COLORS.light.border}`,
      paddingBottom: '12px',
    }}>
      {tabs.map((tab) => (
        <NavLink
          key={tab.path}
          to={tab.path}
          end={tab.path === ''}
          style={({ isActive }) => ({
            padding: '6px 14px', borderRadius: '6px',
            fontFamily: FONTS.heading, fontSize: '11px',
            letterSpacing: '0.12em', textTransform: 'uppercase' as const,
            textDecoration: 'none',
            color: isActive ? '#FFFFFF' : (isDark ? '#B0B0B0' : '#555555'),
            backgroundColor: isActive
              ? (isDark ? 'rgba(1, 169, 237, 0.15)' : 'rgba(1, 101, 198, 0.1)')
              : 'transparent',
            transition: 'background-color 0.2s, color 0.2s',
          })}
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}
